/**
 * Entity writers — Phase 5 replacement.
 * Delegates to /api/entities/* endpoints on dash-api instead of Firestore.
 */

import {getAwpClientHeaders} from '../../session/awpRequestHeaders';
import {sanitizePropertiesDocumentForWrite} from '../../utils/entityProperties.js';

const AWP_BASE = (process.env.REACT_APP_AWP_API_BASE || process.env.REACT_APP_AWP_API_URL || '')
    .replace(/\/$/, '');

const LOG_PREFIX = '[EntityWriters]';

async function apiCall(method, path, body) {
    const res = await fetch(`${AWP_BASE}${path}`, {
        method,
        headers: {
            'Content-Type': 'application/json',
            ...getAwpClientHeaders(),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || err.error || `HTTP ${res.status}`);
    }
    return res.status === 204 ? null : res.json();
}

/**
 * Persist a Wikidata entity's summary to dash-api.
 */
export async function saveEntitySummary(wikidataId, summary) {
    if (!wikidataId || !summary) {
        throw new Error(`${LOG_PREFIX} Missing required params: wikidataId="${wikidataId}"`);
    }
    console.debug(`${LOG_PREFIX} Saving summary for ${wikidataId}`);
    const startTime = performance.now();
    await apiCall('PUT', `/api/entities/${encodeURIComponent(wikidataId)}/summary`, summary);
    const elapsed = Math.round(performance.now() - startTime);
    console.debug(`${LOG_PREFIX} Summary saved for ${wikidataId} in ${elapsed}ms`);
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('kb-entities-changed', {detail: {wikidataId}}));
    }
}

/**
 * Persist a Wikidata entity's properties to dash-api.
 */
export async function saveEntityProperties(wikidataId, properties) {
    if (!wikidataId) {
        throw new Error(`${LOG_PREFIX} Missing required param: wikidataId`);
    }
    console.debug(`${LOG_PREFIX} Saving ${properties?.length ?? 0} properties for ${wikidataId}`);
    const startTime = performance.now();
    const {items, revision} = sanitizePropertiesDocumentForWrite(properties);
    await apiCall('PUT', `/api/entities/${encodeURIComponent(wikidataId)}/properties-doc`, {items, revision});
    const elapsed = Math.round(performance.now() - startTime);
    console.debug(`${LOG_PREFIX} Properties saved for ${wikidataId} (${items.length} items) in ${elapsed}ms`);
}
